import React from "react";
import { Link } from "@/components/LocaleLink";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Users, Building2, HeartHandshake, Siren } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import PartnerStrip from "@/components/PartnerStrip";
import IndsatsCard from "@/components/IndsatsCard";
import { getIndsatser } from "@/data/indsatser";
import FinalCTA from "@/components/FinalCTA";

const IMG = {
  lotus: "https://images.unsplash.com/photo-1538998073820-4dfa76300194?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  therapy: "https://images.unsplash.com/photo-1714976694525-71eb29a7c500?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  pro: "https://images.unsplash.com/photo-1765366417046-f46361a7f26f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const Home = () => {
  const { t, lang } = useLang();
  const cardIcons = [Users, Building2, HeartHandshake];
  const ind = getIndsatser(lang);

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.lotus} alt="" className="h-full w-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#122019]/90 via-[#1A2E24]/75 to-[#1A2E24]/40" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Eyebrow dark>{t.hero.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">
              {t.hero.title}
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-[#EAE5D9] leading-relaxed max-w-2xl">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/ydelser" className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#F7F5F0] text-[#1A2E24] px-8 py-4 font-medium hover:bg-white transition-colors duration-300" data-testid="hero-services-btn">
                {t.hero.primary}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-8 py-4 font-medium hover:bg-white/10 transition-colors duration-300" data-testid="hero-contact-btn">
                {t.hero.secondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <PartnerStrip />

      {/* BESKÆFTIGELSE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32" data-testid="home-beskaeftigelse">
        <div className="max-w-2xl mb-16">
          <Reveal><Eyebrow>{t.beskaeftigelse.label}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#1A2E24] leading-tight">{t.beskaeftigelse.title}</h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {t.beskaeftigelse.cards.map((c, i) => {
            const Icon = cardIcons[i];
            return (
              <Reveal
                key={c.title}
                delay={i * 0.1}
                className={`rounded-3xl border border-black/5 p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                  i === 1 ? "bg-[#2D5A3F] text-white md:mt-8" : "bg-white text-[#1A2E24]"
                }`}
                data-testid={`beskaeftigelse-card-${i}`}
              >
                <span className={`flex items-center justify-center h-12 w-12 rounded-full mb-6 ${i === 1 ? "bg-white/15 text-white" : "bg-[#EAE5D9] text-[#2D5A3F]"}`}>
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl mb-3">{c.title}</h3>
                <p className={`leading-relaxed ${i === 1 ? "text-[#EAE5D9]" : "text-[#3B5245]"}`}>{c.text}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* AKUT BAND */}
      <section className="relative overflow-hidden bg-[#1A2E24] text-white" data-testid="home-akut">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8EA694]">
                <Siren className="h-4 w-4" /> {t.homeAkut.label}
              </span>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl leading-tight">{t.homeAkut.title}</h2>
              <p className="mt-6 text-lg text-[#EAE5D9] leading-relaxed">{t.homeAkut.text}</p>
              <Link to="/akut-projekter" className="group inline-flex items-center gap-2 mt-8 rounded-full bg-[#C47C5D] text-white px-7 py-3.5 font-medium hover:bg-[#b06a4c] transition-colors duration-300" data-testid="akut-readmore">
                {t.common.readMore}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
              <img src={IMG.pro} alt="Lotusgruppen akut projekter" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOCUS CARDS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32" data-testid="home-focus">
        <div className="max-w-2xl mb-14">
          <Reveal><Eyebrow>{t.homeCards.label}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#1A2E24] leading-tight">{t.homeCards.title}</h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {t.homeCards.items.map((item, i) => (
            <Reveal key={item.key} delay={i * 0.1}>
              <Link
                to={`/${item.key === "autisme" ? "autisme" : item.key}`}
                className="group block h-full rounded-3xl overflow-hidden bg-white border border-black/5 hover:shadow-xl transition-all duration-300"
                data-testid={`focus-card-${item.key}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={i === 0 ? IMG.lotus : IMG.therapy} alt={item.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl text-[#1A2E24]">{item.title}</h3>
                    <ArrowUpRight className="h-5 w-5 text-[#8EA694] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <p className="mt-3 text-[#3B5245] leading-relaxed">{item.text}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDSATSER OG FORLØB */}
      <section className="bg-[#EAE5D9] border-y border-black/5" data-testid="home-indsatser">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Reveal><Eyebrow>{ind.overview.eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#1A2E24] leading-tight">{ind.overview.title}</h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/find-forloeb"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C47C5D] text-white px-6 py-3 font-medium hover:bg-[#b06a4c] transition-colors duration-300 whitespace-nowrap"
                  data-testid="home-find-forloeb"
                >
                  {lang === "da" ? "Find det rette forløb" : "Find the right pathway"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/indsatser"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-[#2D5A3F] text-[#2D5A3F] px-6 py-3 font-medium hover:bg-[#2D5A3F] hover:text-white transition-colors duration-300 whitespace-nowrap"
                  data-testid="home-indsatser-all"
                >
                  {ind.labels.backToOverview}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ind.products.map((p, i) => (
              <IndsatsCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FOR KOMMUNER OG KRIMINALFORSORGEN */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-testid="home-kommuner">
        <div className="rounded-3xl bg-[#1A2E24] text-white overflow-hidden">
          <div className="p-8 sm:p-12 lg:p-14 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
            <div>
              <Reveal><Eyebrow dark>{lang === "da" ? "For kommuner og Kriminalforsorgen" : "For municipalities and the Probation Service"}</Eyebrow></Reveal>
              <Reveal delay={0.05}>
                <p className="mt-5 font-display text-3xl sm:text-4xl leading-snug">
                  {lang === "da"
                    ? "Lotusgruppen tilbyder tydeligt beskrevne forløb med fast kontaktperson, løbende dokumentation og tæt koordinering omkring den enkelte indsats."
                    : "Lotusgruppen offers clearly described pathways with a fixed contact person, ongoing documentation and close coordination around each effort."}
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <Link to="/kommuner-og-kriminalforsorgen" className="group inline-flex items-center gap-2 mt-8 rounded-full bg-[#F7F5F0] text-[#1A2E24] px-7 py-3.5 font-medium hover:bg-white transition-colors duration-300" data-testid="home-kommuner-cta">
                  {lang === "da" ? "Læs om samarbejdet" : "Read about the collaboration"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <ul className="space-y-3">
                {(lang === "da"
                  ? ["Tydelige mål og leverancer", "Hurtig og direkte kontakt", "Dokumenteret progression"]
                  : ["Clear goals and deliverables", "Fast and direct contact", "Documented progression"]
                ).map((it) => (
                  <li key={it} className="flex items-center gap-3 rounded-2xl bg-white/[0.05] border border-white/10 px-5 py-4 text-[#EAE5D9]">
                    <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#8EA694]/20 text-[#8EA694] shrink-0"><ArrowUpRight className="h-4 w-4" /></span>
                    {it}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
};

export default Home;
